import { Button } from 'components/ui/button';
import { ArrowLeft, MoreVertical, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import GeneratePodcast from 'features/podcast/modals/GeneratePodcast';
import { useEffect, useState } from 'react';
import CharacterDetails from 'features/podcast/modals/CharacterDetails';
import DiscardPodcast from 'features/podcast/modals/DiscardPodcast';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import CharacterDetailsMobile from 'features/podcast/modals/CharacterDetailsMobile';
import toast from 'react-hot-toast';

interface PodcastHeaderProps {
  isGenerated: boolean;
}

const PodcastHeader = ({ isGenerated }: PodcastHeaderProps) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const [openCharacterDetails, setOpenCharacterDetails] =
    useState<boolean>(false);
  const { podcast_url } = useSelector(
    (state: RootState) => state.podcastPlayback
  );
  const [isMobile, setIsMobile] = useState(false); // to check for mobile

  useEffect(() => {
    if (window.innerWidth < 640) {
      setIsMobile(true);
    }
  }, [window.innerWidth]);

  const handleDownload = () => {
    try {
      if (!podcast_url) {
        toast.error('No podcast available to download');
        return;
      }
      
      const a = document.createElement('a');
      a.href = podcast_url;
      a.download = 'Podcast.mp3';
      
      // Checking if URL is valid
      if (!podcast_url.startsWith('http')) {
        toast.error('Invalid podcast URL');
        return;
      }
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success('Download started');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download podcast');
    }
  };

  return (
    <header className="sm:block flex items-center text-text-primary justify-between sm:items-start sm:justify-start rounded-t-lg border-b-2 h-10 sm:h-full">
      {/* container */}
      <div className="sm:px-4 sm:py-2">
        <div className="h-full sm:h-14 flex flex-row items-center">
          {/* heading */}
          <div className="flex flex-row items-center gap-3 cursor-pointer sm:ml-0 ml-3">
            <ArrowLeft
              color="#846CEC"
              size={15}
              onClick={() => navigate('/ai-podcast-generator/get-started')}
            />

            <h1 className="sm:font-semibold text-text-primary text-[14px] font-normal md:text-2xl">
              {location.pathname === '/ai-podcast/view-podcast' ? (
                'Podcast Generator/preview'
              ) : (
                <span>
                  Podcast Generator
                  <span className="font-bold"> / FinalPodcast</span>
                </span>
              )}
            </h1>
          </div>

          {/* buttons */}
          <div className="flex flex-row gap-5 ml-auto">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setOpenCharacterDetails((prev) => !prev)}
              className="sm:block hidden"
            >
              <span className="text-primary underline underline-offset-2 text-[16px] font-normal">
                Character details
              </span>
            </Button>
            {openCharacterDetails ? (
              isMobile ? (
                <>
                  <div className="absolute left-[calc(50%-240px)] top-[calc(50%-300px)] z-[1000]">
                    <CharacterDetailsMobile
                      setOpen={() => setOpenCharacterDetails(false)}
                    />
                  </div>
                </>
              ) : (
                <div className="">
                  <div className="fixed inset-0 bg-black/50 z-[9]" />
                  <div className="absolute z-10 top-[50%] left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-lg p-4">
                    <button
                      title="close"
                      type="button"
                      className="relative left-[97%] text-[#ffffff] z-10"
                      onClick={() => setOpenCharacterDetails(false)}
                      aria-label="close"
                    >
                      <X size={20} />
                    </button>
                    <CharacterDetails
                      openCharacterDetails={openCharacterDetails}
                    />
                  </div>
                </div>
              )
            ) : null}
            <div className="hidden sm:block">
              <DiscardPodcast />
            </div>

            {/* if no generated podcast screen showing download button */}
            {isGenerated && podcast_url ? (
              <Button
                variant="customSecondary"
                size="sm"
                className="text-[14px] font-normal sm:block hidden bg-secondary"
                onClick={handleDownload}
              >
                Download(.mp3)
              </Button>
            ) : (
              <div className="hidden sm:block">
                <GeneratePodcast />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="sm:hidden ml-auto relative">
        <button
          className="p-2"
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-label="More options"
        >
          <MoreVertical size={20} color="#846CEC" />
        </button>

        {/* Dropdown menu */}
        {menuOpen && (
          <div className="absolute z-10 right-0 mt-2 w-40 bg-white shadow-lg rounded-md border-2">
            <button
              className="w-full text-left px-4 py-2 text-sm text-[#1A211E] hover:bg-gray-100"
              onClick={() => {
                setOpenCharacterDetails(true);
                setMenuOpen(false);
              }}
            >
              Character details
            </button>
          </div>
        )}
      </div>
    </header>
  );
};

export default PodcastHeader;
